import { Section, Container, Eyebrow, DisplayH1, DisplayH2, Reveal } from "@/components/Layout";
import { Link } from "react-router-dom";
import { Award, GraduationCap, Heart, Users } from "lucide-react";
import Timeline from "@/components/Timeline";
import FounderVideo from "@/components/FounderVideo";

export default function About() {
  return (
    <>
      <Section testid="about-hero" className="pt-40 pb-16">
        <Container>
          <Eyebrow>About the University</Eyebrow>
          <DisplayH1 className="mt-6 max-w-4xl">We are the school we always wished existed.</DisplayH1>
          <p className="mt-8 max-w-3xl text-lg text-[color:var(--wk-bone-dim)] leading-relaxed">
            Founded in Chicago by Toriano "The Wolfkeeper" Sanzone, Wolfkeeper University brings
            together behavioral scientists, cinematographers, veterans, and world-class trainers
            under a single standard — that every dog and every family deserves an exceptional education.
          </p>
        </Container>
      </Section>

      {/* FOUNDER FEATURE */}
      <Section testid="about-founder" className="py-24 border-y border-white/10">
        <Container>
          <Reveal className="mb-14">
            <FounderVideo caption="Toriano 'The Wolfkeeper' Sanzone — In His Own Words" testid="about-founder-video" />
          </Reveal>
          <div className="grid lg:grid-cols-12 gap-16 items-start">
            <Reveal className="lg:col-span-6">
              <div className="relative overflow-hidden rounded-3xl">
                <img
                  src="https://customer-assets-v7afamib.emergentagent.net/job_packleader-hub/artifacts/0f7iuk2g_Trailblazer.jpeg"
                  alt="Toriano Sanzone — Humane Society Trailblazer Award Winner 2025"
                  className="w-full h-full object-cover"
                />
              </div>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-6">
              <Eyebrow>Founder · The Wolfkeeper</Eyebrow>
              <DisplayH2 className="mt-4">Toriano Sanzone.</DisplayH2>
              <p className="mt-6 text-lg text-[color:var(--wk-bone-dim)] leading-relaxed">
                Personally mentored by <span className="text-[color:var(--wk-bone)]">Cesar Millan</span>,
                Toriano built the Wolfkeeper method by studying under the world's most recognized
                behaviorist and then translating that mastery into an academic institution the
                world could learn from.
              </p>
              <p className="mt-4 text-lg text-[color:var(--wk-bone-dim)] leading-relaxed">
                From street rescues in Chicago to service dogs deployed with veterans, Toriano's
                life work is now a curriculum, a foundation, and a global community.
              </p>

              <div className="mt-10 grid grid-cols-2 gap-6">
                <div className="card-lux p-6">
                  <Award className="text-[color:var(--wk-gold)]" />
                  <div className="mt-3 font-serif-display text-xl leading-tight">Humane Society<br />Trailblazer 2025</div>
                  <div className="mt-2 text-xs text-[color:var(--wk-bone-dim)]">Cesar's Forum · Las Vegas</div>
                </div>
                <div className="card-lux p-6">
                  <GraduationCap className="text-[color:var(--wk-gold)]" />
                  <div className="mt-3 font-serif-display text-xl leading-tight">Cesar Millan<br />Master Apprentice</div>
                  <div className="mt-2 text-xs text-[color:var(--wk-bone-dim)]">Multi-year mentorship</div>
                </div>
                <div className="card-lux p-6">
                  <Heart className="text-[color:var(--wk-gold)]" />
                  <div className="mt-3 font-serif-display text-xl leading-tight">Golden Wolf<br />Foundation</div>
                  <div className="mt-2 text-xs text-[color:var(--wk-bone-dim)]">Founder & Board Chair</div>
                </div>
                <div className="card-lux p-6">
                  <Users className="text-[color:var(--wk-gold)]" />
                  <div className="mt-3 font-serif-display text-xl leading-tight">Faculty<br />President</div>
                  <div className="mt-2 text-xs text-[color:var(--wk-bone-dim)]">Wolfkeeper University</div>
                </div>
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>

      {/* CESAR MOMENT */}
      <Section testid="about-cesar" className="py-24">
        <Container>
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            <Reveal className="lg:col-span-5">
              <Eyebrow>Lineage</Eyebrow>
              <DisplayH2 className="mt-4">The night the student<br />became the standard.</DisplayH2>
              <p className="mt-6 text-lg text-[color:var(--wk-bone-dim)] leading-relaxed">
                Las Vegas, April 2025. On the closing night of Cesar's Forum, the Humane Society
                of the United States named Toriano "The Wolfkeeper" Sanzone as its
                <span className="text-[color:var(--wk-gold)]"> Trailblazer of the Year </span> —
                the highest honor for those forging new paths in animal welfare.
              </p>
              <blockquote className="mt-8 pl-6 border-l-2 border-[color:var(--wk-gold)] font-serif-display text-2xl italic leading-snug">
                "The Trailblazer in Animal Welfare Award recognizes the unsung heroes in animal protection
                who are breaking barriers and forging new paths toward a more equitable and inclusive
                animal-protection movement."
                <div className="mt-3 not-italic text-sm text-[color:var(--wk-bone-dim)] font-body">— Humane Society of the United States</div>
              </blockquote>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-7">
              <div className="relative overflow-hidden rounded-3xl">
                <img
                  src="https://customer-assets-v7afamib.emergentagent.net/job_packleader-hub/artifacts/foyq47ym_CESAR%20and%20I.jpeg"
                  alt="Toriano Sanzone with Cesar Millan"
                  className="w-full h-auto object-cover"
                />
              </div>
              <div className="mt-4 text-xs text-[color:var(--wk-bone-dim)] italic">
                Left: Toriano "The Wolfkeeper" Sanzone. Center: Cesar Millan. Right: Wolfkeeper faculty. Cesar's Forum, Las Vegas 2025.
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>

      {/* TIMELINE */}
      <Section testid="about-timeline" className="py-24 border-t border-white/10">
        <Container>
          <div className="max-w-3xl mb-16">
            <Eyebrow>The Trailblazer Timeline</Eyebrow>
            <DisplayH2 className="mt-4">From Chicago rescues to the Humane Society stage.</DisplayH2>
            <p className="mt-6 text-lg text-[color:var(--wk-bone-dim)] leading-relaxed">
              Twenty-seven years, six continents, one relentless mission — the making of a
              modern movement in canine welfare.
            </p>
          </div>
          <Timeline />
        </Container>
      </Section>

      {/* STORY */}
      <Section testid="about-story" className="py-24 border-t border-white/10">
        <Container>
          <div className="grid gap-16 lg:grid-cols-2 items-start">
            <Reveal>
              <img src="https://images.unsplash.com/photo-1641667838410-b257ca266e38?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxjaW5lbWF0aWMlMjBkYXJrJTIwZm9yZXN0JTIwd29vZHMlMjBtaXN0fGVufDB8fHx8MTc4NTgxNzM2NXww&ixlib=rb-4.1.0&q=85" alt="" className="rounded-2xl aspect-[4/5] object-cover w-full" />
            </Reveal>
            <Reveal delay={0.1}>
              <DisplayH2>Twenty-four years of proof.</DisplayH2>
              <p className="mt-6 text-[color:var(--wk-bone-dim)] leading-relaxed">
                Every dog that has passed through our doors — from Chicago rescues to Kenyan
                anti-poaching K9s — has become part of an evolving science. We film, we
                document, we peer review, and we teach. We call it cinematic apprenticeship.
              </p>
              <p className="mt-4 text-[color:var(--wk-bone-dim)] leading-relaxed">
                In 2026, we open our online university to the world — the culmination of a
                two-decade project to make the finest canine education globally accessible.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/programs" className="btn-gold">Explore Programs</Link>
                <Link to="/foundation" className="btn-ghost">Support the Foundation</Link>
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>
    </>
  );
}
